import { forwardRef, useMemo } from "react";
import type { ComponentPropsWithRef } from "react";
import classNames from "classnames";
import { useThemeContext } from "@/react/providers/Theme/ThemeContext";
import { Icon } from "@circle-react/components/shared/Icon";
import type { IconType } from "@circle-react/components/shared/Icon";

export interface MenuIconButtonProps extends ComponentPropsWithRef<"button"> {
  ["aria-label"]: string;
  icon: IconType;
  iconClassName?: string;
  isLight?: boolean;
  isSendButton?: boolean;
  shouldAnimateSpin?: boolean;
  size?: number;
}

export const MenuIconButton = forwardRef<
  HTMLButtonElement,
  MenuIconButtonProps
>(
  (
    {
      icon,
      isLight,
      shouldAnimateSpin,
      className,
      disabled,
      size = 20,
      isSendButton,
      ...props
    },
    ref,
  ) => {
    const { isDarkAppearance } = useThemeContext();

    const newIcon = useMemo(() => {
      if (icon === "28-message-send") {
        return isDarkAppearance ? "28-message-send-dark" : "28-message-send";
      }
      return icon;
    }, [icon, isDarkAppearance]);

    return (
      <button
        ref={ref}
        type="button"
        className={classNames(
          "flex size-7 items-center justify-center rounded",
          {
            "hover:bg-tertiary focus-visible:bg-tertiary p-1": !isSendButton,
            "cursor-not-allowed": disabled,
          },
          className,
        )}
        disabled={disabled}
        {...props}
      >
        <Icon
          type={newIcon}
          size={size}
          className={classNames(size === 28 ? "h-7 !w-7" : "size-5", {
            "text-default dark:text-dark": isLight,
            "text-light dark:text-default": !isLight,
            "animate-spin": shouldAnimateSpin,
            "!text-brand": isSendButton && isLight,
            "!text-disabled": isSendButton && !isLight,
          })}
        />
      </button>
    );
  },
);

MenuIconButton.displayName = "MenuIconButton";
